import { __extends } from "tslib";
import { AssetEvent } from '@awayjs/core';
import { MethodBase, _Shader_MethodBase } from './MethodBase';
/**
 * AmbientBasicMethod provides the default shading method for uniform ambient lighting.
 */
var AmbientBasicMethod = /** @class */ (function (_super) {
    __extends(AmbientBasicMethod, _super);
    /**
     * Creates a new AmbientBasicMethod object.
     */
    function AmbientBasicMethod() {
        var _this = _super.call(this) || this;
        _this._alpha = 1;
        _this._strength = 1;
        return _this;
    }
    Object.defineProperty(AmbientBasicMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return AmbientBasicMethod.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AmbientBasicMethod.prototype, "strength", {
        /**
         * The strength of the ambient reflection of the surface.
         */
        get: function () {
            return this._strength;
        },
        set: function (value) {
            if (this._strength == value)
                return;
            this._strength = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AmbientBasicMethod.prototype, "alpha", {
        /**
         * The alpha component of the surface.
         */
        get: function () {
            return this._alpha;
        },
        set: function (value) {
            if (this._alpha == value)
                return;
            this._alpha = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AmbientBasicMethod.prototype, "texture", {
        /**
         * The texture to use to define the diffuse reflection color per texel.
         */
        get: function () {
            return this._texture;
        },
        set: function (value) {
            if (this._texture == value)
                return;
            if (this._texture)
                this.iRemoveTexture(this._texture);
            this._texture = value;
            if (this._texture)
                this.iAddTexture(this._texture);
            this.invalidateShaderProgram();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * @inheritDoc
     */
    AmbientBasicMethod.prototype.copyFrom = function (method) {
    };
    AmbientBasicMethod.assetType = '[asset AmbientBasicMethod]';
    return AmbientBasicMethod;
}(MethodBase));
export { AmbientBasicMethod };
import { ShaderBase, TextureCube } from '@awayjs/renderer';
/**
 * _Shader_AmbientBasicMethod provides the default shading method for uniform ambient lighting.
 */
var _Shader_AmbientBasicMethod = /** @class */ (function (_super) {
    __extends(_Shader_AmbientBasicMethod, _super);
    /**
     * Creates a new _Shader_AmbientBasicMethod object.
     */
    function _Shader_AmbientBasicMethod(method, shader) {
        var _this = this;
        var _a;
        _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        _this._texture = (_a = _this._method.texture) === null || _a === void 0 ? void 0 : _a.getAbstraction(_this._shader);
        return _this;
    }
    _Shader_AmbientBasicMethod.prototype.onInvalidate = function (event) {
        var _a;
        _super.prototype.onInvalidate.call(this, event);
        var texture = (_a = this._method.texture) === null || _a === void 0 ? void 0 : _a.getAbstraction(this._shader);
        if (this._texture != texture) {
            if (this._texture) {
                this._texture.onClear(null);
                this._texture = null;
            }
            this._texture = texture;
        }
    };
    /**
     *
     */
    _Shader_AmbientBasicMethod.prototype.onClear = function (event) {
        _super.prototype.onClear.call(this, event);
        if (this._texture) {
            this._texture.onClear(new AssetEvent(AssetEvent.CLEAR, this._method.texture));
            this._texture = null;
        }
        this._method = null;
        this._shader = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_AmbientBasicMethod.prototype._initVO = function (chunkVO) {
        if (this._texture) {
            this._texture._initVO(chunkVO);
            if (this._method.texture instanceof TextureCube)
                chunkVO.needsNormals = true;
            else
                this._shader.uvDependencies++;
        }
    };
    /**
     * @inheritDoc
     */
    _Shader_AmbientBasicMethod.prototype._initConstants = function () {
        if (this._texture)
            this._texture._initConstants();
    };
    /**
     * @inheritDoc
     */
    _Shader_AmbientBasicMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code = '';
        if (this._texture) {
            code += this._texture._getFragmentCode(targetReg, registerCache, sharedRegisters, (this._method.texture instanceof TextureCube) ? sharedRegisters.normalFragment : sharedRegisters.uvVarying);
            if (this._shader.alphaThreshold > 0) {
                var cutOffReg = registerCache.getFreeFragmentConstant();
                this._colorIndex = cutOffReg.index * 4;
                code += 'sub ' + targetReg + '.w, ' + targetReg + '.w, ' + cutOffReg + '.x\n' +
                    'kil ' + targetReg + '.w\n' +
                    'add ' + targetReg + '.w, ' + targetReg + '.w, ' + cutOffReg + '.x\n';
            }
        }
        else {
            var ambientInputRegister = registerCache.getFreeFragmentConstant();
            this._colorIndex = ambientInputRegister.index * 4;
            code += 'mov ' + targetReg + ', ' + ambientInputRegister + '\n';
        }
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_AmbientBasicMethod.prototype._activate = function () {
        if (this._texture) {
            this._texture.activate();
            if (this._invalid && this._shader.alphaThreshold > 0)
                this._shader.fragmentConstantData[this._colorIndex] = this._shader.alphaThreshold;
        }
        else if (this._invalid) {
            var index = this._colorIndex;
            var data = this._shader.fragmentConstantData;
            var color = this._shader.numLights ? 0xFFFFFF : this._shader.renderMaterial.style.color;
            data[index] = ((color >> 16) & 0xff) / 0xff * this._method.strength;
            data[index + 1] = ((color >> 8) & 0xff) / 0xff * this._method.strength;
            data[index + 2] = (color & 0xff) / 0xff * this._method.strength;
            data[index + 3] = this._method.alpha;
        }
    };
    _Shader_AmbientBasicMethod.prototype._setRenderState = function (renderState) {
        if (this._texture)
            this._texture._setRenderState(renderState);
    };
    return _Shader_AmbientBasicMethod;
}(_Shader_MethodBase));
export { _Shader_AmbientBasicMethod };
ShaderBase.registerAbstraction(_Shader_AmbientBasicMethod, AmbientBasicMethod);
